import React, { useState, useEffect, useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import fuelIndexService, {
  FuelIndexRow,
  mergeFuelIndexRows,
} from '../../services/fuelIndex.service';
import { XCircleIcon } from '@heroicons/react/24/outline';

/**
 * Current Fuel Index — V1 fuelcharge.pl port.
 *
 * V1 ships two separate pages over the same fueldata.fuelindex table ("Fuel
 * Indexes" and "Driver Fuel Rates"). 360 shows them as ONE table: the weekly
 * index readings, then a "Customer FSC" header group (Effective + FSC) and a
 * "Driver" header group (Gulf Local / OTR / Southeast OTR), so one week reads
 * across in a single line. Grouped headers follow the DispatchVisibility
 * pattern -- a group def carries `id`, `header`, `meta.headerBgClass` and a
 * nested `columns` array.
 *
 * The Effective window sits under Customer FSC because it is a customer-FSC
 * concept only -- V1 renders it on the Fuel Charge view and nowhere on Driver
 * Fuel Rates.
 *
 * Read-only: the rate bands behind these numbers are maintained on
 * /data-management/fuel-surcharge-customer and -driver.
 */

// V1 shows the index to 3 decimals and every rate/FSC to 1. A week with no
// matching rate band comes back null and stays blank rather than reading 0.0.
const fmt = (v: unknown, decimals: number): string =>
  typeof v === 'number' ? v.toFixed(decimals) : '';

const num = (decimals: number) => (info: { getValue: () => unknown }) => (
  <span className="block text-right tabular-nums">{fmt(info.getValue(), decimals)}</span>
);

// Group tints, so the Customer FSC and Driver bands read as distinct blocks
// across a wide row (same meta keys DispatchVisibility uses).
const CUSTOMER_TINT = {
  headerBgClass: 'bg-emerald-50 dark:bg-emerald-900/30',
  cellBgClass: 'bg-emerald-50 dark:bg-emerald-900/20',
  cellBgClassZebra: 'bg-emerald-100 dark:bg-emerald-900/40',
};

const DRIVER_TINT = {
  headerBgClass: 'bg-sky-50 dark:bg-sky-900/30',
  cellBgClass: 'bg-sky-50 dark:bg-sky-900/20',
  cellBgClassZebra: 'bg-sky-100 dark:bg-sky-900/40',
};

const FuelIndex: React.FC = () => {
  const [rows, setRows] = useState<FuelIndexRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [customer, driver] = await Promise.all([
          fuelIndexService.getCustomerFuelIndex(controller.signal),
          fuelIndexService.getDriverFuelRates(controller.signal),
        ]);
        if (cancelled) return;
        setRows(mergeFuelIndexRows(customer, driver));
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        console.error('Error loading fuel index:', err);
        setError('Could not load the fuel index. Please try again.');
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const columns = useMemo<ColumnDef<FuelIndexRow>[]>(
    () => [
      {
        // Sorting and date filters run on the ISO beginDate, never the label.
        accessorKey: 'beginDate',
        id: 'beginDate',
        header: 'Week of',
        enableSorting: true,
        enableColumnFilter: true,
        cell: (info) => info.row.original.weekOf,
      },
      {
        accessorKey: 'nationalIndex',
        header: 'National Index',
        enableSorting: true,
        cell: num(3),
      },
      {
        accessorKey: 'gulfIndex',
        header: 'Gulf Index',
        enableSorting: true,
        cell: num(3),
      },
      { accessorKey: 'midwestIndex', header: 'Midwest Index', enableSorting: true, cell: num(3) },
      { accessorKey: 'eastCoastIndex', header: 'East Coast Index', enableSorting: true, cell: num(3) },
      { accessorKey: 'rockyMountainIndex', header: 'Rocky Mountain Index', enableSorting: true, cell: num(3) },
      { accessorKey: 'westCoastIndex', header: 'West Coast Index', enableSorting: true, cell: num(3) },
      { accessorKey: 'newEnglandIndex', header: 'New England Index', enableSorting: true, cell: num(3) },
      { accessorKey: 'centralAtlanticIndex', header: 'Central Atlantic Index', enableSorting: true, cell: num(3) },
      { accessorKey: 'lowerAtlanticIndex', header: 'Lower Atlantic Index', enableSorting: true, cell: num(3) },
      { accessorKey: 'westCoastLessCaliforniaIndex', header: 'West Coast Less California Index', enableSorting: true, cell: num(3) },
      {
        id: 'customerFsc',
        header: 'Customer FSC',
        meta: { headerBgClass: CUSTOMER_TINT.headerBgClass },
        columns: [
          {
            accessorKey: 'effective',
            header: 'Effective',
            enableSorting: false,
            enableColumnFilter: true,
            meta: CUSTOMER_TINT,
          },
          {
            accessorKey: 'fsc',
            header: 'FSC',
            enableSorting: true,
            meta: CUSTOMER_TINT,
            cell: num(1),
          },
        ],
      },
      {
        id: 'driver',
        header: 'Driver',
        meta: { headerBgClass: DRIVER_TINT.headerBgClass },
        columns: [
          {
            accessorKey: 'gulfLocal',
            header: 'Gulf Local',
            enableSorting: true,
            meta: DRIVER_TINT,
            cell: num(1),
          },
          {
            accessorKey: 'gulfOtr',
            header: 'Gulf OTR',
            enableSorting: true,
            meta: DRIVER_TINT,
            cell: num(1),
          },
          {
            accessorKey: 'southeastOtr',
            header: 'Southeast OTR',
            enableSorting: true,
            meta: DRIVER_TINT,
            cell: num(1),
          },
        ],
      },
    ],
    []
  );

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-body">
          Current Fuel Index
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Weekly fuel index readings with the customer fuel surcharge and driver fuel rates
          they map to. <span className="font-medium">Effective</span> is the week the reading
          is billed for and applies to the customer FSC only.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <XCircleIcon className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 dark:text-red-300 flex-1">{error}</p>
        </div>
      )}

      <TanTable
        data={rows}
        columns={columns}
        loading={loading}
        settingsKey="fuel-index-table"
        fieldName="fuel-index-table"
        cardTitle="Current Fuel Index"
        searchPlaceholder="Search fuel index..."
        defaultSorting={[{ id: 'beginDate', desc: true }]}
        enableGrouping={false}
        enableColumnFilters={true}
      />
    </div>
  );
};

export default FuelIndex;
